import { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Breadcrumb from "@/components/Breadcrumb";
import { msalInstance } from "@/main";
import { useRecalculateAllPrices, useUnlockApp } from "@/hooks/adminHooks";
import PartMigrationPanel from "@/components/admin/PartMigrationPanel";
import ExchangeRatePartMigrationPanel from "@/components/admin/ExchangeRatePartMigrationPanel";
import {
  FontTopic,
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import { useToast } from "@/hooks/useToast";
import { getAxiosErrorMessage } from "@/utils/healper";

const AdminPage = () => {
  const { showToast } = useToast();

  useEffect(() => {
    document.title = "Admin | AMPA";
  }, []);

  const recalculateMutation = useRecalculateAllPrices();
  const unlockMutation = useUnlockApp();

  const handleCopyToken = async (tokenType: "access" | "id") => {
    try {
      const account =
        msalInstance.getActiveAccount() ||
        msalInstance.getAllAccounts()[0] ||
        null;

      const res = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        ...(account ? { account } : {}),
      });

      const token = tokenType === "access" ? res.accessToken : res.idToken;
      await navigator.clipboard.writeText(token);
      showToast(
        `คัดลอก ${tokenType === "access" ? "Access Token" : "ID Token"} แล้ว`,
        "success"
      );
    } catch {
      showToast("ไม่สามารถดึง Token ได้ กรุณา Login ใหม่", "error");
    }
  };

  const handleUnlock = async () => {
    const confirm = await ConfirmAlert(
      "ยืนยันการปลดล็อคระบบ",
      "คุณต้องการปลดล็อคระบบหรือไม่? ใช้เมื่อระบบค้างจากการคำนวณราคา",
      "ปลดล็อค",
      "ยกเลิก",
    );
    if (confirm) {
      unlockMutation.mutate(undefined, {
        onSuccess: () => showToast("ปลดล็อคระบบเรียบร้อยแล้ว", "success"),
        onError: (error) =>
          ErrorAlert("ปลดล็อคล้มเหลว", getAxiosErrorMessage(error)),
      });
    }
  };

  const handleRecalculate = async () => {
    const confirm = await ConfirmAlert(
      "ยืนยันการ Recalculate",
      "คุณต้องการ Recalculate ราคาทั้งโปรเจคหรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    );
    if (confirm) {
      recalculateMutation.mutate(
        { recalculate_child_parts: true },
        {
          onSuccess: () =>
            showToast("คำนวณราคาใหม่ทั้งโปรเจคเรียบร้อยแล้ว", "success"),
          onError: (error) =>
            ErrorAlert("Recalculate ล้มเหลว", getAxiosErrorMessage(error)),
        },
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumb current="Admin Debug" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Token Section */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography sx={{ ...FontTopic, mb: 2 }}>Token</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              sx={DefaultSecondaryButton}
              onClick={() => handleCopyToken("access")}
            >
              คัดลอก Access Token
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              sx={DefaultSecondaryButton}
              onClick={() => handleCopyToken("id")}
            >
              คัดลอก ID Token
            </Button>
          </Box>
        </Paper>

        {/* Recalculate Section */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography sx={{ ...FontTopic, mb: 2 }}>คำนวณราคา</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={
                recalculateMutation.isPending ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <RefreshIcon />
                )
              }
              sx={DefaultPrimaryButton}
              onClick={handleRecalculate}
              disabled={recalculateMutation.isPending}
            >
              {recalculateMutation.isPending
                ? "กำลัง Recalculate..."
                : "Recalculate ราคาทั้งโปรเจค"}
            </Button>
            <Typography variant="body2" color="text.secondary">
              คำนวณราคาใหม่ทั้งหมด รวมถึง Child Parts
            </Typography>
          </Box>
        </Paper>

        {/* Unlock Section */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography sx={{ ...FontTopic, mb: 2 }}>ปลดล็อคระบบ</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={
                unlockMutation.isPending ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <LockOpenIcon />
                )
              }
              sx={DefaultPrimaryButton}
              onClick={handleUnlock}
              disabled={unlockMutation.isPending}
            >
              {unlockMutation.isPending ? "กำลังปลดล็อค..." : "ปลดล็อคระบบ"}
            </Button>
            <Typography variant="body2" color="text.secondary">
              ใช้เมื่อระบบค้างล็อคหลังจากคำนวณราคา
            </Typography>
          </Box>
        </Paper>

        {/* Migration Section */}
        <PartMigrationPanel />
        <ExchangeRatePartMigrationPanel />
        <PartMigrationPanel activeFilter={0} />
        <ExchangeRatePartMigrationPanel activeFilter={0} />
      </Box>

    </Box>
  );
};

export default AdminPage;
